import translation from 'locales/translation';
import {
  Button,
  ButtonThemes,
  CheckCircleIcon,
  Checkbox,
  Col,
  Collapse,
  ControlLabel,
  ControlLabelPosition,
  Modal,
  ModalSizes,
  RadioButton,
  RadioButtonType,
  RefreshIcon,
  Row,
  Select,
  ThreeDotsLoader,
  ToastTypes,
  useDropOpened,
} from 'plataforma-fundacao-componentes';

import { CircleTimes } from 'components/Icons';

import { AxiosError } from 'axios';
import LayoutContext from 'components/Layout/LayoutContext';
import { ModalProps } from 'plataforma-fundacao-componentes/dist/components/modal/Modal';
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import {
  RegisterAcclaimProps,
  allowAvailability,
  registerAcclaim,
} from 'services/assessor';
import {
  COMPUTE_VOTES_LIST,
  COMPUTE_VOTES_NUCLEOS_VALUES,
  COMPUTE_VOTES_SET_FILTER,
  loadPresenceAgency,
  loadPresenceList,
  loadPresenceNucleo,
} from 'store/modules/ComputeVotesActions';
import ComputeVotesReducer, {
  INITIAL_STATE,
  IProps as IPropsComputeVotesReducer,
} from 'store/modules/ComputeVotesReducer';
import ListVotes, { STATUS_LIST_VOTES } from './components/ListVotes/ListVotes';
import './styles.scss';

const rootClassName = 'componente-compute-votes-modal';

interface IProps extends ModalProps {
  idPauta: number;
  titlePauta: string;
  eventType: any;
  isReadOnly: boolean;
}

interface ListVotesProps {
  agencia: string;
  nucleo: string;
  totalAssociados: number;
  flagSemVotos: boolean;
  status: STATUS_LIST_VOTES;
  ldap: string;
  itens: any;
}

interface VoteProps {
  idItemPauta: number;
  descricao: string;
  quantidadeVotos: number;
  flgAclamacao: boolean;
}

type ActiveListProps = {
  agencia: string;
  nucleo: string;
};

export const ComputeVotes = (props: IProps) => {
  const [state, dispatch] = useReducer(ComputeVotesReducer, INITIAL_STATE);
  const [activeList, setActiveList] = useState<ActiveListProps>();
  const [loadingSave, setLoadingSave] = useState<boolean>(false);
  const [registerAllNucleous, setRegisterAllNucleous] =
    useState<boolean>(false);
  const [idItemPauta, setIdItemPauta] = useState<number | null>();
  const { showToast } = useContext(LayoutContext);

  const handleClose = () => {
    if (typeof props.onClose === 'function') {
      props.onClose(props.modalKey);
    }

    if (activeList && activeList.agencia) {
      allowAvailability({
        agencia: activeList!?.agencia!,
        idPauta: props.idPauta,
        nucleo: activeList!?.nucleo!,
      });
    }
  };

  const partitionArr = (array: Array<any>, isValid: (evt?: any) => {}) => {
    return array.reduce(
      (
        [_completed, _notCompleted]: ListVotesProps[][],
        elem: ListVotesProps
      ) => {
        return isValid(elem)
          ? [[..._completed, elem], _notCompleted]
          : [_completed, [..._notCompleted, elem]];
      },
      [[], []]
    );
  };

  const handleLoadList = useCallback(() => {
    setRegisterAllNucleous(false);

    return loadPresenceList(showToast, state, dispatch);
  }, [showToast, state]);

  useEffect(() => {
    dispatch({
      type: COMPUTE_VOTES_SET_FILTER,
      payload: {
        idPauta: props.idPauta,
      },
    });
  }, [props.idPauta]);

  useEffect(() => {
    if (state.filter.idPauta) {
      loadPresenceAgency(dispatch, state, showToast);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.idPauta, state.filter.idPauta]);

  useEffect(() => {
    if (state.filter.agencia) {
      loadPresenceNucleo(showToast, state, dispatch);
    } else {
      dispatch({
        type: COMPUTE_VOTES_SET_FILTER,
        payload: {
          nucleos: null,
        },
      });
      dispatch({
        type: COMPUTE_VOTES_LIST,
        payload: [],
      });
      dispatch({
        type: COMPUTE_VOTES_NUCLEOS_VALUES,
        payload: [],
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.filter.agencia]);

  useEffect(() => {
    if (state.filter.agencia) {
      handleLoadList();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.filter.agencia, state.filter.nucleos]);

  const [completed, notCompleted] = useMemo(
    () =>
      partitionArr(
        state.items,
        (e) =>
          e.status === STATUS_LIST_VOTES.CONCLUIDA ||
          e.status === STATUS_LIST_VOTES.IMPORTADA
      ),
    [state.items]
  );

  const showText = useMemo(() => {
    return state.items
      .filter((x) => {
        if (state.filter.agencia && !state.filter.nucleos) {
          return x.agencia === state.filter.agencia;
        }

        if (state.filter.agencia && state.filter.nucleos) {
          return (
            x.agencia === state.filter.agencia &&
            x.nucleo === state.filter.nucleos
          );
        }

        return false;
      })
      .some(
        (x) =>
          x.status === STATUS_LIST_VOTES.CONCLUIDA ||
          x.status === STATUS_LIST_VOTES.IMPORTADA
      );
  }, [state.filter.agencia, state.filter.nucleos, state.items]);

  const handleRegisterAcclaim = useCallback(
    (request: RegisterAcclaimProps) => {
      setLoadingSave(true);

      return registerAcclaim(request)
        .then(() => {
          setLoadingSave(false);
          handleLoadList();
        })
        .catch((error) => {
          setLoadingSave(false);
          showToast({
            pauseOnFocusLoss: true,
            theme: ToastTypes.Error,
            showStatusBar: true,
            timeout: 4000,
            label:
              translation.Toasts.Errors.Keys[
                `${
                  (error as AxiosError<Error>).response?.data.message ||
                  (error as AxiosError<Error>).response?.data ||
                  (error as AxiosError<Error>).message
                }` as keyof typeof translation.Toasts.Errors.Keys
              ] ?? translation.Toasts.Errors.defaultAlternative,
          });
        });
    },
    [handleLoadList, showToast]
  );

  useEffect(() => {
    if (!registerAllNucleous) {
      setIdItemPauta(null);
    }
  }, [registerAllNucleous]);

  return (
    <Modal
      preventEscExit={props.preventEscExit || false}
      preventMaskExit={props.preventMaskExit || false}
      modalKey={props.modalKey}
      mobileOn={props.mobileOn}
      title={props.title}
      className={rootClassName}
      onClose={() => {
        props?.onClose && props.onClose();
        handleClose();
      }}
      compenseColPaddingContent
      footer={[
        {
          theme: ButtonThemes.Secondary,
          leftIcon: <CircleTimes />,
          children: translation.ComputeVotes.buttons.cancel,
          'aria-label': translation.ComputeVotes.buttons.cancel,
          onClick: handleClose,
          disabled: state.itemsLoading,
        },
        {
          theme: ButtonThemes.Secondary,
          leftIcon: <RefreshIcon animate={state.itemsLoading} />,
          children: translation.ComputeVotes.buttons.update,
          'aria-label': translation.ComputeVotes.buttons.update,
          onClick: handleLoadList,
          disabled: !state.filter.agencia || state.itemsLoading,
        },
      ]}
      size={ModalSizes.Large}
    >
      <Row className={`${rootClassName}-modal-content`}>
        <Col className={`${rootClassName}-header-content`}>
          <h2>{props.titlePauta}</h2>
          <h4>
            <strong>OBS: </strong> {translation.ComputeVotes.description}
          </h4>
        </Col>
        <Col cols={[6, 6, 6, 6, 6]}>
          <Select
            data-testid={'select-agency'}
            placeholder={translation.ComputeVotes.filter.agencia}
            value={state.filter.agencia}
            options={state.agenciaOptions}
            loading={state.agenciaOptionsLoading}
            onChange={(evt) => {
              dispatch({
                type: COMPUTE_VOTES_SET_FILTER,
                payload: {
                  agencia: evt,
                },
              });
            }}
            panelFilter={state.agenciaOptions.length > 3}
            clearButton
          />
        </Col>
        <Col cols={[6, 6, 6, 6, 6]}>
          <Select
            data-testid={'select-nuc'}
            placeholder={translation.ComputeVotes.filter.nucleo}
            clearButton
            disabled={!state.filter.agencia || !state.nucleosOptions}
            value={state.filter.nucleos}
            options={state.nucleosOptions}
            loading={state.nucleosOptionsLoading}
            onChange={(evt) => {
              dispatch({
                type: COMPUTE_VOTES_SET_FILTER,
                payload: {
                  nucleos: evt,
                },
              });
            }}
            panelFilter={state.nucleosOptions.length > 3}
          />
        </Col>
        {state.itemsLoading ? (
          <Col centralized>
            <ThreeDotsLoader />
          </Col>
        ) : (
          <Fragment>
            {!notCompleted.length && completed.length ? (
              <Col className={`${rootClassName}-header-content center`}>
                <h2>{translation.ComputeVotes.allVotes}</h2>
                <div />
              </Col>
            ) : undefined}

            <Collapse
              opened={
                notCompleted.length && state.filter.agencia ? true : false
              }
              animateOpacity
            >
              <Row>
                <Collapse
                  opened={Boolean(
                    props.eventType === 'AGN' &&
                      !props.isReadOnly &&
                      notCompleted.length &&
                      state.filter.agencia &&
                      !state.filter.nucleos
                  )}
                  animateOpacity
                >
                  <Col style={{ justifyContent: 'space-between' }}>
                    <ControlLabel
                      label={'Registrar aclamação para os núcleos abaixo:'}
                      control={
                        <Checkbox
                          disabled={Boolean(
                            props.eventType !== 'AGN' &&
                              (props.isReadOnly ||
                                !notCompleted.length ||
                                !state.filter.agencia ||
                                state.filter.nucleos)
                          )}
                          value={registerAllNucleous}
                          onChange={() =>
                            setRegisterAllNucleous((e: boolean) => !e)
                          }
                        />
                      }
                      labelPosition={ControlLabelPosition.Right}
                      tooltip-text={
                        'A aclamação ocorrerá apenas nos núcleos sem votos.'
                      }
                    />
                  </Col>
                </Collapse>
              </Row>
              <Row>
                <Collapse opened={registerAllNucleous} animateOpacity>
                  <Col>
                    <Row className={`${rootClassName}-votes-list-border`}>
                      <Row className={`${rootClassName}-votes-list`}>
                        {notCompleted.length &&
                          notCompleted[0].itens!?.map(
                            (item: any, index: number) => {
                              return (
                                <Col
                                  onClick={() => {
                                    if (!props.isReadOnly)
                                      setIdItemPauta(item.idItemPauta);
                                  }}
                                  key={index}
                                  className={`${rootClassName}-votes-container`}
                                >
                                  <RadioButton
                                    data-testid={`radio-vote-${index}`}
                                    theme={RadioButtonType.New}
                                    value={idItemPauta === item.idItemPauta}
                                    onChange={() => {
                                      setIdItemPauta(item.idItemPauta);
                                    }}
                                    disabled={props.isReadOnly}
                                  />
                                  <div
                                    className={`${rootClassName}-votes-options`}
                                  >
                                    <strong>{item.descricao}</strong>
                                  </div>
                                </Col>
                              );
                            }
                          )}
                        <Col buttonActionsCol end>
                          <Button
                            leftIcon={<CheckCircleIcon />}
                            disabled={
                              props.isReadOnly || loadingSave || !idItemPauta
                            }
                            loading={loadingSave}
                            onClick={() => {
                              handleRegisterAcclaim({
                                idPauta: state.filter.idPauta,
                                agencia: state.filter.agencia,
                                itens: notCompleted[0].itens.map(
                                  (item: {
                                    idItemPauta: number;
                                    flagAclamacao: boolean;
                                  }) =>
                                    item.idItemPauta === idItemPauta
                                      ? {
                                          idItemPauta: item.idItemPauta,
                                          flagAclamacao: true,
                                        }
                                      : {
                                          idItemPauta: item.idItemPauta,
                                          flagAclamacao: false,
                                        }
                                ),
                              });
                            }}
                          >
                            Salvar
                          </Button>
                        </Col>
                      </Row>
                    </Row>
                  </Col>
                  <Col>
                    <div className={'divider'} />
                  </Col>
                </Collapse>
              </Row>

              <Col className={`${rootClassName}-list-content`}>
                <MapListVotes
                  listVotesProps={notCompleted}
                  state={state}
                  props={props}
                  handleLoadList={handleLoadList}
                  registerAllNucleous={registerAllNucleous}
                  setActiveList={setActiveList}
                />
              </Col>
            </Collapse>

            {showText ? (
              <Col className={`${rootClassName}-header-content`}>
                <h2>{translation.ComputeVotes.completedVote}</h2>
              </Col>
            ) : undefined}

            {completed.length ? (
              <Fragment>
                <Col className={`${rootClassName}-list-content`}>
                  <MapListVotes
                    listVotesProps={completed}
                    state={state}
                    props={props}
                    handleLoadList={handleLoadList}
                    setActiveList={setActiveList}
                  />
                </Col>
              </Fragment>
            ) : undefined}
          </Fragment>
        )}
      </Row>
    </Modal>
  );
};

type MapListVotesProps = {
  listVotesProps: ListVotesProps[];
  state: IPropsComputeVotesReducer;
  props: IProps;
  handleLoadList: () => Promise<void>;
  registerAllNucleous?: boolean;
  setActiveList: (props: ActiveListProps) => void;
};

const MapListVotes = ({
  listVotesProps,
  state,
  props,
  handleLoadList,
  registerAllNucleous = false,
  setActiveList,
}: MapListVotesProps) => {
  const [opened, setOpened] = useDropOpened<string | undefined>(undefined);

  return (
    <>
      {listVotesProps
        .filter((x: { agencia: string; nucleo: string }) => {
          if (state.filter.agencia && !state.filter.nucleos) {
            return x.agencia === state.filter.agencia;
          }

          if (state.filter.agencia && state.filter.nucleos) {
            return (
              x.agencia === state.filter.agencia &&
              x.nucleo === state.filter.nucleos
            );
          }
          return false;
        })
        .map((option: ListVotesProps, index: number) => {
          return (
            <ListVotes
              dataTestId={`not-completed-list-${index}`}
              key={index}
              agencia={option.agencia}
              nucleo={option.nucleo}
              idPauta={state.filter.idPauta}
              flagSemVotos={option.flagSemVotos}
              opened={String(opened) === String(`${index}-${option.status}`)}
              status={option.status}
              totalAssociados={option.totalAssociados}
              borderColor={option.status}
              toggleOpened={(val) => {
                setOpened(`${index}-${option.status}`, val);
                setActiveList({
                  agencia: option.agencia,
                  nucleo: option.nucleo,
                });
              }}
              ldap={option.ldap}
              registerAllNucleous={registerAllNucleous}
              itens={option?.itens?.map((item: VoteProps) => {
                return {
                  descricao: item.descricao,
                  quantidadeVotos: item.quantidadeVotos,
                  idItemPauta: item.idItemPauta,
                  flgAclamacao: item.flgAclamacao,
                };
              })}
              onRefresh={handleLoadList}
              onVoteButtonSave={() =>
                new Promise((resolve) => {
                  handleLoadList();
                  resolve();
                })
              }
              disabledAclamacao={['AGO', 'AOE', 'AGE'].includes(
                props.eventType
              )}
              disabledNoVote={['AGO', 'AOE', 'AGE'].includes(props.eventType)}
              isReadOnly={props.isReadOnly}
            />
          );
        })}
    </>
  );
};
